import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { EyeOff, FilePen, Gavel, History, ShieldOff } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  CHANGE_NOTE_MAX,
  OVERALL_MARK_MAX,
  INTERVIEW_FLAG_LABELS,
  PANELIST_ROLE_LABELS,
  RECOMMENDATION_LABELS,
  SCORECARD_STATUS_LABELS,
  WAIVER_REASON_MAX,
  submitProblems,
  type CockpitRubricView,
  type RoundScorecardView,
  type ScorecardDraft,
  type ScorecardVersionView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Drawer } from '../../../components/Drawer';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { Popover, usePopover } from '../../../components/Popover';
import { SkeletonLines } from '../../../components/Skeleton';
import { INTERVIEW_FLAG_TONES, RECOMMENDATION_TONES, SCORECARD_STATUS_TONES } from '../../../components/status-tones';
import { ToneChip, type Tone } from '../../../components/Tone';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { EMPTY, formatDateTime } from '../../../lib/format';
import { useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { interviewScorecardsKey, scorecardsApi, useInterviewScorecards } from '../api/scorecards-api';
import { ScorecardForm } from '../components/ScorecardForm';
import styles from './Scorecards.module.css';

/** A round whose decision is recorded is finished: the scorecards are closed for good. */
const DECIDED_TONE: Tone = 'done';

/**
 * INT-03 — every scorecard on one interview, read-only, with version history. The HR
 * Lead waives a missing one here (F11.11) and corrects a submitted one (F11.08). Where
 * N-40 lands until RPT-04 exists (S29). Drafts never show: only submitted versions do.
 */
export function InterviewScorecardsPage({ interviewId }: { readonly interviewId: string }): ReactNode {
  const query = useInterviewScorecards(interviewId);
  const navigate = useNavigate();
  const canSeeDecisions = useCanView('stage_decision');

  if (query.isPending) return <SkeletonLines count={10} />;
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="these scorecards" contact="The recruiter for this job" />;
    if (query.error.status === 404) return <NotFoundState onGoBack={() => void navigate({ to: '/interviews' })} />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  const view = query.data;
  return (
    <div className={styles.screen}>
      <Link to="/interviews/$interviewId" params={{ interviewId }} className={styles.back}>
        ← The interview
      </Link>
      <Card title={`Scorecards — ${view.candidateName}`} meta={`${view.jobTitle} · ${view.roundName}${view.completedAt ? ` · completed ${formatDateTime(view.completedAt)}` : ''}`}>
        <div className={styles.row}>
          {view.isDecided && <ToneChip tone={DECIDED_TONE}>Decision recorded — no more changes</ToneChip>}
          {view.flags.map((flag) => (
            <ToneChip key={flag.kind} tone={INTERVIEW_FLAG_TONES[flag.kind]}>
              {INTERVIEW_FLAG_LABELS[flag.kind]}
              {flag.note ? ` — ${flag.note}` : ''}
            </ToneChip>
          ))}
          {view.isOnPanel && (
            <Link to="/interviews/$interviewId/scorecard" params={{ interviewId }} className={styles.linkButton}>
              <FilePen size={16} aria-hidden /> Open your scorecard
            </Link>
          )}
          {canSeeDecisions && (view.status === 'completed' || view.status === 'panelist_no_show') && (
            <Link to="/interviews/$interviewId/debrief" params={{ interviewId }} className={styles.linkButton}>
              <Gavel size={16} aria-hidden /> {view.isDecided ? 'The decision' : 'Debrief and decide'}
            </Link>
          )}
        </div>
      </Card>
      {view.scorecards.length === 0 && <p className={styles.quiet}>Nobody is on this interview's panel.</p>}
      {/* ADR 0077 §5 (D94): blind — the server sends only your own scorecard until you submit it. */}
      {view.isBlind && (
        <p className={styles.quiet}>
          <EyeOff size={14} aria-hidden /> The rest of the panel's scorecards open to you once you submit your own.
        </p>
      )}
      {view.scorecards.map((seat) => (
        <SeatCard key={seat.scorecardId} interviewId={interviewId} seat={seat} rubric={view.rubric} />
      ))}
    </div>
  );
}

function SeatCard({ interviewId, seat, rubric }: { readonly interviewId: string; readonly seat: RoundScorecardView; readonly rubric: CockpitRubricView | null }): ReactNode {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isCorrecting, setIsCorrecting] = useState(false);
  const [latest, ...older] = seat.versions;
  return (
    <Card
      title={seat.panelistName}
      meta={PANELIST_ROLE_LABELS[seat.role]}
      action={
        <div className={styles.row}>
          <ToneChip tone={statusTone(seat)}>{statusWords(seat)}</ToneChip>
          {seat.can.correct && (
            <Button size="sm" variant="outline" icon={FilePen} onClick={() => setIsCorrecting(true)}>
              Correct
            </Button>
          )}
          {seat.can.waive && <WaiveButton interviewId={interviewId} seat={seat} />}
        </div>
      }
    >
      {seat.isHiddenUntilYouSubmit && (
        <p className={styles.quiet}>
          <EyeOff size={14} aria-hidden /> Submitted. The panel's marks open to you once you submit your own.
        </p>
      )}
      {seat.status === 'waived' && (
        <p className={styles.quiet}>
          Waived by {seat.waivedByName ?? 'HR'}: "{seat.waiverReason}". Left out of the round.
        </p>
      )}
      {seat.status === 'draft' && !seat.isHiddenUntilYouSubmit && <p className={styles.quiet}>{seat.dueAt ? `Due ${formatDateTime(seat.dueAt)}.` : 'Not submitted yet.'}</p>}
      {latest && <VersionBody version={latest} rubric={rubric} />}
      {older.length > 0 && (
        <div className={styles.history}>
          <Button size="sm" variant="ghost" icon={History} onClick={() => setIsHistoryOpen((open) => !open)} aria-expanded={isHistoryOpen}>
            {isHistoryOpen ? 'Hide earlier versions' : `Earlier versions (${older.length})`}
          </Button>
          {isHistoryOpen && older.map((version) => <VersionBody key={version.versionNumber} version={version} rubric={rubric} isEarlier />)}
        </div>
      )}
      {latest && isCorrecting && <CorrectDrawer interviewId={interviewId} seat={seat} latest={latest} onClose={() => setIsCorrecting(false)} />}
    </Card>
  );
}

function VersionBody({ version, rubric, isEarlier = false }: { readonly version: ScorecardVersionView; readonly rubric: CockpitRubricView | null; readonly isEarlier?: boolean }): ReactNode {
  return (
    <div className={`${styles.version} ${isEarlier ? styles.earlier : ''}`}>
      <div className={styles.row}>
        <span className={styles.versionTitle}>Version {version.versionNumber}</span>
        <span className={styles.quiet}>
          {formatDateTime(version.submittedAt)}
          {version.isByAuthor ? '' : ` · by ${version.submittedByName ?? 'HR'}`}
        </span>
        {version.recommendation && <ToneChip tone={RECOMMENDATION_TONES[version.recommendation]}>{RECOMMENDATION_LABELS[version.recommendation]}</ToneChip>}
        {version.isObserver && <Chip tone="neutral">Observer — does not count</Chip>}
        {/* C5: one overall mark; a version filled before it shows its rubric score. */}
        <span className={styles.score}>{version.overallMark !== null ? `${version.overallMark} of ${OVERALL_MARK_MAX}` : version.panelistScore === null ? EMPTY : version.panelistScore.toFixed(2)}</span>
      </div>
      {version.changeNote && <p className={styles.quiet}>What changed: {version.changeNote}</p>}
      {rubric && version.criteria.length > 0 && (
        <dl className={styles.criteria}>
          {rubric.criteria.map((criterion) => {
            const entry = version.criteria.find((item) => item.criterionId === criterion.id);
            return (
              <div key={criterion.id} className={styles.criterion}>
                <dt>{criterion.label}</dt>
                <dd>
                  <span className={styles.score}>{entry?.isNotAssessed ? 'Not assessed' : entry?.score ?? EMPTY}</span>
                  {entry?.comment && <span className={styles.comment}>{entry.comment}</span>}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
      <p className={styles.notes}>{version.notes ?? EMPTY}</p>
    </div>
  );
}

/** F11.11 — HR Lead only, reason mandatory; the chase must have reached them first. */
function WaiveButton({ interviewId, seat }: { readonly interviewId: string; readonly seat: RoundScorecardView }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const queryClient = useQueryClient();
  const toasts = useToasts();
  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  async function waive(): Promise<void> {
    setIsSaving(true);
    try {
      const next = await scorecardsApi.waive(seat.scorecardId, { reason: reason.trim() });
      queryClient.setQueryData(interviewScorecardsKey(interviewId), next);
      close();
      toasts.show({ tone: 'success', message: `${seat.panelistName}'s scorecard waived`, detail: 'The round is decided on the others.' });
    } catch (error) {
      toasts.show({ tone: 'danger', message: "Couldn't waive it", detail: errorMessage(error) });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <Button ref={triggerRef} size="sm" variant="danger" icon={ShieldOff} onClick={toggle}>
        Waive
      </Button>
      <Popover anchor={anchor} onClose={close} label="Waive this scorecard" align="right">
        <div className={styles.panel}>
          <p className={styles.panelText}>Waiving leaves {seat.panelistName}'s scorecard out, so the round can be decided on the others. It is recorded with your reason and cannot be undone.</p>
          <Field label="Why" isRequired>
            {({ inputId }) => <Textarea id={inputId} rows={3} value={reason} maxLength={WAIVER_REASON_MAX} placeholder="e.g. On leave until next month" onChange={(event) => setReason(event.target.value)} />}
          </Field>
          <div className={styles.panelActions}>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button variant="dangerSolid" disabled={reason.trim() === ''} isPending={isSaving} onClick={() => void waive()}>
              Waive scorecard
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}

/** F11.08 by the HR Lead: the next version, with the reason it changed. */
function CorrectDrawer({ interviewId, seat, latest, onClose }: { readonly interviewId: string; readonly seat: RoundScorecardView; readonly latest: ScorecardVersionView; readonly onClose: () => void }): ReactNode {
  const queryClient = useQueryClient();
  const toasts = useToasts();
  // C5 (ADR 0077): a correction is one overall mark and a comment, as every new version is.
  const [draft, setDraft] = useState<ScorecardDraft>(() => ({ criteria: [], recommendation: null, notes: latest.notes ?? '', overallMark: latest.overallMark }));
  const [changeNote, setChangeNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const problems = submitProblems(draft, seat.role === 'observer');

  async function save(): Promise<void> {
    setIsSaving(true);
    try {
      const next = await scorecardsApi.correct(seat.scorecardId, { basedOnVersion: latest.versionNumber, draft, changeNote: changeNote.trim() });
      queryClient.setQueryData(interviewScorecardsKey(interviewId), next);
      toasts.show({ tone: 'success', message: `Version ${latest.versionNumber + 1} of ${seat.panelistName}'s scorecard saved`, detail: `Version ${latest.versionNumber} is kept.` });
      onClose();
    } catch (error) {
      toasts.show({ tone: 'danger', message: "Couldn't save the correction", detail: errorMessage(error) });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={`Correct ${seat.panelistName}'s scorecard`}
      meta={`Makes version ${latest.versionNumber + 1}; version ${latest.versionNumber} is kept`}
      footer={
        <div className={styles.panelActions}>
          {problems.length > 0 && <span className={styles.problem}>{problems[0]}</span>}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="solid" disabled={changeNote.trim() === '' || problems.length > 0} isPending={isSaving} onClick={() => void save()}>
            Save as version {latest.versionNumber + 1}
          </Button>
        </div>
      }
    >
      <div className={styles.drawerBody}>
        <Field label="What you changed and why" isRequired hint="The panelist and every report see this.">
          {({ inputId, describedBy }) => <Textarea id={inputId} aria-describedby={describedBy} rows={2} value={changeNote} maxLength={CHANGE_NOTE_MAX} onChange={(event) => setChangeNote(event.target.value)} />}
        </Field>
        <ScorecardForm draft={draft} onChange={setDraft} isReadOnly={false} isObserver={seat.role === 'observer'} isMarkMissing={false} isCommentMissing={false} />
      </div>
    </Drawer>
  );
}

function statusTone(seat: RoundScorecardView): Tone {
  if (seat.status === 'draft' && seat.chaseLevel > 0) return 'urgent';
  return SCORECARD_STATUS_TONES[seat.status];
}

function statusWords(seat: RoundScorecardView): string {
  if (seat.status === 'draft' && seat.chaseLevel > 0) return 'Overdue';
  const label = SCORECARD_STATUS_LABELS[seat.status];
  return seat.status === 'submitted' && seat.versions.length > 1 ? `${label} · v${seat.versions.length}` : label;
}
